import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { FontAwesome6 } from '@react-native-vector-icons/fontawesome6/static';
import Ionicons from '@react-native-vector-icons/ionicons';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { GTFonts, useTheme, withOpacity, type Theme } from '../../../theme';
import { fs, iconSize, s, v } from '../../../utils/libs';

/**
 * Wizard final step (US4, T067). Recaps the workspace concepts and adapts its
 * copy to whether a product was created. The "Go to Home" action is owned by
 * the host shell's primary button.
 *
 * New Sale / Back Office icons + colors match the Home owner CTAs; Home stays
 * the existing outline/primary treatment.
 */

export interface SetupCompleteStepProps {
  /** True when a real product was created during the wizard. */
  productCreated: boolean;
}

type ConceptId = 'home' | 'new_sale' | 'back_office';

const CONCEPTS: Array<{
  id: ConceptId;
  title: string;
  description: string;
}> = [
  {
    id: 'home',
    title: 'Home',
    description: 'Your daily launch pad and Getting Started guide.',
  },
  {
    id: 'new_sale',
    title: 'New Sale',
    description: 'Build a basket and check out in seconds.',
  },
  {
    id: 'back_office',
    title: 'Back Office',
    description: 'Products, staff, reports, and settings.',
  },
];

// const NEXT_TASKS: { id: string; label: string }[] = [
//   { id: 'add_product', label: 'Add a product' },
//   { id: 'first_sale', label: 'Make your first real sale' },
//   { id: 'explore_backoffice', label: 'Explore Back Office' },
//   { id: 'add_staff', label: 'Add a staff member' },
//   { id: 'visit_test_location', label: 'Try the test location' },
// ];

const SetupCompleteStep: React.FC<SetupCompleteStepProps> = ({
  productCreated,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  return (
    <View style={styles.root} testID="setup-complete-step">
      <View style={styles.badgeRow}>
        <View style={styles.badge}>
          <Ionicons
            name="checkmark-circle"
            size={iconSize(28)}
            color={theme.colors.success}
          />
        </View>
        <Text style={styles.productCopy}>
          {productCreated
            ? 'Your first product is ready — you can sell it right away.'
            : 'You can add your first product any time.'}
        </Text>
      </View>

      <Text style={styles.sectionTitle}>Where things live</Text>
      <View style={styles.conceptList}>
        {CONCEPTS.map(concept => {
          const accent =
            concept.id === 'back_office'
              ? theme.colors.successEmerald
              : theme.colors.primary;

          return (
            <View key={concept.id} style={styles.conceptRow}>
              <View
                style={[
                  styles.conceptIcon,
                  { backgroundColor: withOpacity(accent, 0.1) },
                ]}>
                {concept.id === 'home' ? (
                  <Ionicons
                    name="home-outline"
                    size={iconSize(20)}
                    color={theme.colors.primary}
                  />
                ) : concept.id === 'new_sale' ? (
                  <MaterialCommunityIcons
                    name="plus-circle"
                    size={iconSize(22)}
                    color={theme.colors.primary}
                  />
                ) : (
                  <FontAwesome6
                    iconStyle="solid"
                    name="circle-dollar-to-slot"
                    size={iconSize(20)}
                    color={theme.colors.successEmerald}
                  />
                )}
              </View>
              <View style={styles.conceptText}>
                <Text style={styles.conceptTitle}>{concept.title}</Text>
                <Text style={styles.conceptDescription}>
                  {concept.description}
                </Text>
              </View>
            </View>
          );
        })}
      </View>

      {/*
      <Text style={styles.sectionTitle}>What's next on Home</Text>
      <View style={styles.previewCard} testID="setup-complete-tracker-preview">
        <Text style={styles.previewHeading}>Getting Started</Text>
        {NEXT_TASKS.map(task => {
          const done = productCreated && task.id === 'add_product';
          return (
            <View key={task.id} style={styles.previewRow}>
              <Ionicons
                name={done ? 'checkmark-circle' : 'ellipse-outline'}
                size={iconSize(18)}
                color={done ? theme.colors.success : theme.colors.textSecondary}
              />
              <Text
                style={[styles.previewLabel, done && styles.previewLabelDone]}>
                {task.label}
              </Text>
            </View>
          );
        })}
      </View>
      */}
    </View>
  );
};

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    root: {
      gap: v(16),
    },
    badgeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(12),
      marginBottom: v(16),
    },
    badge: {
      width: s(44),
      height: s(44),
      borderRadius: s(22),
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: withOpacity(theme.colors.success, 0.12),
    },
    productCopy: {
      flex: 1,
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    sectionTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(20),
      color: theme.colors.text,
    },
    conceptList: {
      gap: v(12),
    },
    conceptRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: s(12),
    },
    conceptIcon: {
      width: iconSize(36),
      height: iconSize(36),
      borderRadius: theme.borderRadius.md,
      alignItems: 'center',
      justifyContent: 'center',
    },
    conceptText: {
      flex: 1,
      gap: v(2),
    },
    conceptTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    conceptDescription: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
    },
    // previewCard: {
    //   gap: v(10),
    //   padding: s(14),
    //   borderRadius: theme.borderRadius.md,
    //   backgroundColor: theme.colors.surface,
    //   borderWidth: 1,
    //   borderColor: theme.colors.borderSecondary,
    // },
    // previewHeading: {
    //   fontFamily: GTFonts.semibold,
    //   fontSize: fs(14),
    //   lineHeight: fs(18),
    //   color: theme.colors.text,
    // },
    // previewRow: {
    //   flexDirection: 'row',
    //   alignItems: 'center',
    //   gap: s(10),
    // },
    // previewLabel: {
    //   flex: 1,
    //   fontFamily: GTFonts.regular,
    //   fontSize: fs(13),
    //   lineHeight: fs(18),
    //   color: theme.colors.text,
    // },
    // previewLabelDone: {
    //   color: theme.colors.text,
    //   textDecorationLine: 'line-through',
    // },
  });

export default SetupCompleteStep;
